import * as React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';

export type TrendDirection = 'up' | 'flat' | 'down';

/**
 * Direction-of-travel badge for a metric.
 *
 * The reason this is a component rather than a `className` is that
 * red/green coding is the classic colour-blind failure — deuteranopia and
 * protanopia affect roughly 8% of men, for whom the two tints are close to
 * identical. So every pill carries THREE redundant signals:
 *
 *   1. colour   (teal / amber / red tint)
 *   2. an arrow (up / flat / down)
 *   3. a sign   (+ / ± / −) baked into the formatted value
 *
 * Any one of those alone would be a bug. Routing every trend through here
 * is what stops someone hand-rolling a bare coloured percentage.
 *
 * `semantic` exists because up is not always good: rising cancellations or
 * a rising dispute rate should read as bad. Pass `semantic="inverse"` and
 * the arrow still points up while the colour goes red.
 */
export function TrendPill({
  /** Percentage change. 4.2 renders as "+4.2%", -1.5 as "−1.5%". */
  value,
  /** Whether a rising number is good ('normal') or bad ('inverse'). */
  semantic = 'normal',
  /** Anything within this many percent of zero counts as flat. */
  flatThreshold = 0.05,
  /** Context for screen readers, e.g. "vs last month". */
  comparisonLabel,
  /** 'points' for a change in a rate (50% → 75% is +25 points, not +25%). */
  unit = 'percent',
  className,
}: {
  value: number;
  semantic?: 'normal' | 'inverse';
  flatThreshold?: number;
  comparisonLabel?: string;
  unit?: 'percent' | 'points';
  className?: string;
}) {
  const isFlat = Math.abs(value) <= flatThreshold;
  const rising = value > 0;

  const direction: TrendDirection = isFlat ? 'flat' : rising ? 'up' : 'down';
  // Colour follows MEANING; the arrow follows the NUMBER. They deliberately
  // disagree when a rising metric is bad.
  const tone: TrendDirection = isFlat
    ? 'flat'
    : semantic === 'inverse'
      ? (rising ? 'down' : 'up')
      : direction;

  const TONES: Record<TrendDirection, string> = {
    up: 'bg-kanfy-trend-up text-kanfy-trend-up-fg',
    flat: 'bg-kanfy-trend-flat text-kanfy-trend-flat-fg',
    down: 'bg-kanfy-trend-down text-kanfy-trend-down-fg',
  };
  const Icon = isFlat ? Minus : rising ? TrendingUp : TrendingDown;

  const sign = isFlat ? '±' : rising ? '+' : '−';
  const magnitude = Math.abs(value).toFixed(1);
  const text = `${sign}${magnitude}${unit === 'points' ? ' pts' : '%'}`;

  // The visible text uses U+2212 MINUS, which many screen readers skip.
  // The label spells the direction out instead of relying on the glyph.
  const spoken = `${isFlat ? 'No change' : rising ? 'Up' : 'Down'} ${magnitude} ${unit === 'points' ? 'percentage points' : 'percent'}${
    comparisonLabel ? ` ${comparisonLabel}` : ''
  }`;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold tabular-nums',
        TONES[tone],
        className,
      )}
    >
      <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />
      <span aria-hidden="true">{text}</span>
      <span className="sr-only">{spoken}</span>
    </span>
  );
}
